/**
 * Text & Slideshow — a stack of photographs that cross-fade on their own,
 * with no prev/next control and no counter. That is the block's whole
 * point: it is asked for where a single static photo would otherwise sit.
 *
 * The absence of a visible pause control is a deliberate choice (recorded
 * as A11Y-016 in docs/accessibility-findings.html), not an oversight, so
 * two things stand in for it without adding one:
 *
 *  - It honours prefers-reduced-motion. Automatically rotating photographs
 *    is exactly what that setting is asking about, so there the first
 *    photo just stays put.
 *  - It pauses while the pointer or keyboard focus is anywhere inside the
 *    block, and resumes when both leave. Nothing about that is a control a
 *    viewer has to find; it is just what already happens when you stop to
 *    read the copy next to it or tab through a link inside it.
 *
 * Only the photograph actually on screen is exposed to a screen reader.
 * The rest carry `aria-hidden`, moved here rather than left for CSS: an
 * `opacity: 0` layer is still in the accessibility tree, and reading out
 * five photographs whose alt text describes something no longer visible
 * is worse than reading one.
 *
 * No optional chaining or nullish coalescing: gulp-babel has no preset
 * configured, so uglify-js parses this as written.
 */
(function () {
  const blocks = document.querySelectorAll(".ts-photo");
  if (!blocks.length) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const INTERVAL = 5000;
  function setup(well) {
    const slides = well.querySelectorAll("img");
    if (slides.length < 2) return;
    const section = well.closest(".text-slideshow");
    let index = 0;
    let timer = null;
    function show(next) {
      const current = slides[index];
      current.classList.remove("is-active");
      current.setAttribute("aria-hidden", "true");
      index = next;
      const incoming = slides[index];
      incoming.classList.add("is-active");
      incoming.removeAttribute("aria-hidden");
    }
    function start() {
      if (reduceMotion || timer) return;
      timer = setInterval(function () {
        show((index + 1) % slides.length);
      }, INTERVAL);
    }
    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    // The first slide is already the one showing (both in the no-JS
    // markup and in `.ts-photo img:first-child`'s CSS), so this only ever
    // needs to add the class the script itself will look for from here on.
    slides[0].classList.add("is-active");
    start();
    const pauseTarget = section || well;
    pauseTarget.addEventListener("mouseenter", stop);
    pauseTarget.addEventListener("mouseleave", start);
    pauseTarget.addEventListener("focusin", stop);
    pauseTarget.addEventListener("focusout", start);
  }
  for (let i = 0; i < blocks.length; i++) {
    setup(blocks[i]);
  }
})();